import React, { useContext, useEffect, useState } from "react";
import { makeStyles, Theme, createStyles, Paper, Container, Typography, TableCell } from "@material-ui/core";
import { observer } from "mobx-react";
import { RootContext } from "../../../../../stores";
import { getVolunteerDetailsLink } from "../../../../Navigation/Links/UrlConstructors";
import { ShiftDetails } from "../../../../../stores/models/ShiftDetails";
import { AccountPlus, AccountRemove } from "mdi-material-ui";
import RecordTable from "../RecordTable";
import { Option } from "../../../../../stores/models/Option";
import { OptionCollection } from "../../../../../stores/models/OptionCollection";
import { ISortableTableHeader, TableHeader } from "../../../../../stores/models/TableHeader";
import { VolunteerShiftRegistration } from "../../../../../stores/models/VolunteerShiftRegistration";
import ConfirmationDialog from "../../../../Shared/ConfirmationDialog";
import UpdateStatusPopper from "../UpdateStatusPopper";
import { IStatus } from "../../../../../stores/models/Status";
import { getInitialDialogStates } from "../../../../../logic/DialogStateDictionary";
import { useNavigateInternally } from "../../../../Navigation/Hooks";
import REMToolbar from "../../../../../shared/modules/rem-conversion/components/Toolbar";
import REMButton from "../../../../../shared/modules/rem-conversion/components/Button";
import EmailsButton from "./EmailsButton";
import AdminVolunteerRegistrationFlow from "../../../../../modules/flows/admin-volunteer-registration/components/AdminVolunteerRegistrationFlow";
import { IShift, Shift } from "../../../../../stores/models/Shift";
import { DialogState } from "../../../../../stores/models/DialogState";
import SuccessSnackbar from "../../../../Shared/SuccessSnackbar";
import clsx from "clsx";
import { useStandardStyles } from "../../../../../shared/general/hooks/useStandardStyles";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        paper: {
            paddingTop: '15px',
            paddingBottom: '15px',
            whiteSpace: 'pre-line',
            wordBreak: 'break-word'
        },
        section: {
            paddingTop: '15px'
        },
        selectionMenu: {
            marginLeft: theme.spacing(-0.5),
            '& > button.MuiButton-outlined': {
                color: theme.palette.action.active,
            },
            '& > *': {
                margin: theme.spacing(.5)
            }
        },
    })
);

const headCells = ([
    { id: 'select', alignment: 'center', disablePadding: true, label: '', sortable: false, checkbox: true },
    { id: 'volunteerName', alignment: 'left', disablePadding: false, label: 'Volunteer', sortable: true, sortValue: 'volunteerName' },
    { id: 'email', alignment: 'left', disablePadding: false, label: 'Email', sortable: true, sortValue: 'email' },
    { id: 'status', alignment: 'left', disablePadding: false, label: 'Status', sortable: true, sortValue: 'status' },
] as ISortableTableHeader<VolunteerShiftRegistration>[]).map(headCell => new TableHeader(headCell));

enum DialogTypes {
    UnregisterConfirmation = 'unregisterConfirmation',
    RegisterVolunteersPopper = 'registerVolunteersPopper',
    StatusPopper = 'statusPopper'
}

interface VolunteerRegistrationsSectionProps {
    shiftDetails: ShiftDetails;
    onShiftUpdated: (newShift: ShiftDetails, oldShift?: ShiftDetails) => void;
}

const VolunteerRegistrationsSection = observer((props: VolunteerRegistrationsSectionProps) => {

    /********* React hooks *********/
    const classes = useStyles();
    const baseClasses = useStandardStyles();
    const rootStore = useContext(RootContext);
    const shiftStore = rootStore.shiftStore;
    const navigate = useNavigateInternally();

    /********* State *********/

    const [dialogStates] = useState((() => {
        const states = getInitialDialogStates(Object.values(DialogTypes));
        states.registerVolunteersPopper = new DialogState({ open: false, loading: false, title: "Register Volunteers" });
        return states;
    })());

    const shiftDetails = props.shiftDetails;

    const getAllVolunteerRegistrations = () => {
        return shiftDetails ? shiftDetails.volunteerRegistrations : [];
    }

    const [volunteerRegistrationsCollection, setVolunteerRegistrationsCollection] = useState(new OptionCollection('volunteerId', getAllVolunteerRegistrations()));
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    /********* Effects *********/

    useEffect(() => {
        setVolunteerRegistrationsCollection(new OptionCollection('volunteerId', getAllVolunteerRegistrations()));
    }, [shiftDetails?.shift.shiftRegistrations, shiftDetails?.volunteerRegistrations]);

    /********* API Requests *********/

    const updateRegistrationStatuses = async (status?: IStatus) => {
        if (!status || !shiftDetails) return;

        const selections = volunteerRegistrationsCollection.selectedOptions;
        const volunteerIds = selections.map(selection => selection.volunteerId);
        const updatedRegistrations = await shiftStore.updateVolunteerRegistrationStatuses(volunteerIds, shiftDetails.shift.identificationData, status.id);
        return updatedRegistrations;
    }

    /********* Event handlers *********/

    const onVolunteerClicked = (volunteerRegistration: VolunteerShiftRegistration) => {
        navigate(getVolunteerDetailsLink(volunteerRegistration.volunteerId));
    }

    /********* Volunteer Unregistration *********/

    const onUnregisterClicked = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        dialogStates.unregisterConfirmation.setOpen(true);
    }

    const onUnregistrationConfirmed = async () => {
        dialogStates.unregisterConfirmation.setLoading(true);
        await onRecruiterUnregisteredVolunteers();
        dialogStates.unregisterConfirmation.setLoading(false);
    }

    const onRecruiterUnregisteredVolunteers = async () => {
        const unregisteredStatus = shiftStore.volunteerRegistrationStatuses.find(status => status.status === 'Unregistered');
        const updatedRegistrations = await updateRegistrationStatuses(unregisteredStatus);

        if (!updatedRegistrations) return;
        if (updatedRegistrations.shift) {
            onVolunteerRegistrationsChanged(updatedRegistrations.shift, true);
        }
        dialogStates.unregisterConfirmation.setOpen(false);
    }

    /********* Volunteer Registration Status Changes *********/

    const onStatusUpdate = async (status: IStatus) => {
        if (!shiftDetails || !shiftDetails.topLevelShift.shiftRegistrations) return;

        dialogStates.statusPopper.setLoading(true);
        const updatedRegistrations = await updateRegistrationStatuses(status);
        dialogStates.statusPopper.setLoading(false);

        if (updatedRegistrations && status) {
            applyVolunteerStatusUpdate(status);
            dialogStates.statusPopper.setOpen(false);
        }
    }

    const applyVolunteerStatusUpdate = (status: IStatus) => {
        if (!shiftDetails) return;

        volunteerRegistrationsCollection.selections.forEach(registration => {
            const volunteerId = registration.object.volunteerId;
            const matchingVolunteerRegistration = shiftDetails.volunteerRegistrations.find(volunteerRegistration => volunteerRegistration.volunteerId === volunteerId);
            if (matchingVolunteerRegistration) {
                matchingVolunteerRegistration.setStatus(status.status);
            }
        });
        volunteerRegistrationsCollection.setSelections([]);
    }

    /********* Volunteer Registration *********/

    const onVolunteerRegistrationsChanged = (result: IShift, unregistered?: boolean) => {
        const shiftIdentificationData = shiftDetails?.shift.identificationData;
        const shift = new Shift(result);
        if (shiftDetails) {
            if (shift.isOneOffShift) {
                shiftDetails.setShift(shift);
            } else {
                const parentShift = shift;
                shiftDetails.setParentShift(parentShift);
                const currentInstance = parentShift.getInstanceByIdentificationData(shiftIdentificationData);
                if (currentInstance) {
                    shiftDetails.setShift(currentInstance);
                }
            }
            props.onShiftUpdated(shiftDetails);
        }
        if (unregistered !== true) {
            setSnackbarOpen(true);
        }
    }

    /********* Emails Button *********/

    const emails = volunteerRegistrationsCollection.selectedOptions.length > 0
        ? volunteerRegistrationsCollection.selectedOptions.filter(record => record.email !== null && record.email !== undefined).map(record => record.email!)
        : volunteerRegistrationsCollection.optionObjects.filter(record => record.email !== null && record.email !== undefined).map(record => record.email!);

    const emailsButton = (
        <EmailsButton
            emails={emails}
            numSelected={
                volunteerRegistrationsCollection.selectedOptions.length > 0
                    ? volunteerRegistrationsCollection.selectedOptions.length
                    : volunteerRegistrationsCollection.options.length
            }
        />
    )

    /********* Render *********/

    return (
        <React.Fragment>
            {/* Registrations */}
            <Paper className={classes.paper}>
                <Container>
                    <Typography variant="h5">
                        Registrations
                    </Typography>
                    {/* Toolbar Buttons */}
                    <REMToolbar
                        className={baseClasses.flex}
                        disableGutters
                    >
                        {volunteerRegistrationsCollection.selections.length > 0
                            ? <div className={clsx(classes.selectionMenu, baseClasses.flex, baseClasses.flexWrap)}>
                                <REMButton
                                    startIcon={<AccountRemove />}
                                    variant="outlined"
                                    size="large"
                                    onClick={onUnregisterClicked}
                                >
                                    Unregister
                                </REMButton>
                                <UpdateStatusPopper
                                    statuses={shiftStore.filteredRegistrationStatuses}
                                    popperState={dialogStates.statusPopper}
                                    onConfirm={onStatusUpdate}
                                />
                                {emailsButton}
                                {/* <Button
                                                                startIcon={<Email />}
                                                                variant="outlined"
                                                                size="large"
                                                            // TODO: implement onClick
                                                            >
                                                                Email
                                                            </Button> */}
                            </div>
                            : <div className={clsx(classes.selectionMenu, baseClasses.flex, baseClasses.flexWrap)}>
                                <REMButton
                                    startIcon={<AccountPlus />}
                                    variant={"contained"}
                                    size={"large"}
                                    color={'primary'}
                                    onClick={() => dialogStates.registerVolunteersPopper.setOpen(true)}
                                >
                                    Register Volunteers
                                </REMButton>
                                {emailsButton}
                            </div>
                        }
                    </REMToolbar>
                    <AdminVolunteerRegistrationFlow
                        dialogState={dialogStates.registerVolunteersPopper}
                        registeredVolunteerIds={volunteerRegistrationsCollection.optionObjects.map(registration => registration.volunteerId)}
                        topLevelShift={shiftDetails.topLevelShift}
                        offset={shiftDetails.shift.identificationData.defaultDaysFromStartDate}
                        onVolunteersRegistered={onVolunteerRegistrationsChanged}
                    />
                    <SuccessSnackbar
                        open={snackbarOpen}
                        message={'Volunteers registered!'}
                        onClose={() => setSnackbarOpen(false)}
                    />
                    {/* Registrations Table */}
                    <RecordTable
                        records={volunteerRegistrationsCollection}
                        tableHeaderCells={headCells}
                        orderBy={'volunteerName'}
                        order={'asc'}
                        displayFunctions={[
                            (serviceEntry: Option<VolunteerShiftRegistration>, rowIndex: number, cellIndex: number) => {
                                return null;
                            },
                            (option: Option<VolunteerShiftRegistration>, rowIndex: number, cellIndex: number) => {
                                return (
                                    <TableCell align="left" key={`${rowIndex}-${cellIndex}`}>
                                        {option.object.volunteerName}
                                    </TableCell>
                                );
                            },
                            (option: Option<VolunteerShiftRegistration>, rowIndex: number, cellIndex: number) => {
                                return (
                                    <TableCell align="left" key={`${rowIndex}-${cellIndex}`}>
                                        {option.object.email}
                                    </TableCell>
                                );
                            },
                            (option: Option<VolunteerShiftRegistration>, rowIndex: number, cellIndex: number) => {
                                return (
                                    <TableCell align="left" key={`${rowIndex}-${cellIndex}`}>
                                        {option.object.status}
                                    </TableCell>
                                );
                            },
                        ]}
                        onRowClicked={(option: Option<VolunteerShiftRegistration>, rowIndex: number) => {
                            onVolunteerClicked(option.object);
                        }}
                    />
                </Container>
            </Paper>
            <ConfirmationDialog
                state={dialogStates.unregisterConfirmation}
                title={volunteerRegistrationsCollection.selectedOptions.length > 1
                    ? "Unregister Volunteers"
                    : "Unregister Volunteer"
                }
                content={volunteerRegistrationsCollection.selectedOptions.length > 1
                    ? <Typography>Are you sure you want to unregister these volunteers?</Typography>
                    : <Typography>Are you sure you want to unregister this volunteer?</Typography>
                }
                confirmText={'Unregister'}
                onConfirm={onUnregistrationConfirmed}
                fullScreen={false}
            />
        </React.Fragment>
    );
});

export default VolunteerRegistrationsSection;